import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { AppIcon } from "@/components/AppIcon";
import { Link, ShoppingCart, Wifi, Store } from "lucide-react";
import aiRecipeImage from "@/assets/ai-recipe-generation.jpg";
import mealPlanningImage from "@/assets/meal-planning.jpg";
import pantryManagementImage from "@/assets/pantry-management.jpg";
import recipeManagementImage from "@/assets/recipe-management.jpg";

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      {/* Header */}
      <header className="flex items-center justify-end p-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/auth')}
            className="text-muted-foreground hover:text-foreground"
          >
            Sign In
          </Button>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Content with Logo */}
          <div className="space-y-8">
            <div className="mb-12">
              <AppIcon size={120} />
            </div>
            
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-7xl font-semibold text-foreground leading-tight">
                Smart Recipe
                <br />
                <span className="text-muted-foreground font-light">Management</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-lg leading-relaxed">
                Transform your cooking with AI-powered recipe generation, smart meal planning, 
                and intelligent pantry management. Your culinary journey starts here.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/auth')}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-4 text-lg"
              >
                Start Cooking Smart
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="rounded-full px-8 py-4 text-lg"
              >
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[600px]">
              <img 
                src="/lovable-uploads/769da161-3ed1-43a6-9307-32bed66b94db.png" 
                alt="Professional kitchen environment" 
                className="w-full h-full object-cover"
                fetchPriority="high"
                width={612}
                height={600}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent"></div>
              {/* Watermark cover overlay */}
              <div className="absolute bottom-0 right-0 w-32 h-16 bg-gradient-to-tl from-background/90 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Top 4 Features */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-foreground mb-4">Everything you need to cook smarter</h2>
            <p className="text-xl text-muted-foreground">Powerful features designed for modern home chefs</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={aiRecipeImage} 
                  alt="AI Recipe Generation" 
                  className="w-full h-48 object-cover"
                  loading="lazy"
                  width={296}
                  height={192}
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3 text-center">AI Recipe Generation</h3>
                  <p className="text-muted-foreground text-center leading-relaxed">
                    Generate personalized recipes using AI based on your available ingredients, dietary preferences, and cooking skill level.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={mealPlanningImage} 
                  alt="Smart Meal Planning" 
                  className="w-full h-48 object-cover"
                  loading="lazy"
                  width={296}
                  height={192}
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3 text-center">Smart Meal Planning</h3>
                  <p className="text-muted-foreground text-center leading-relaxed">
                    Plan your meals for days or weeks ahead with drag-and-drop simplicity. Automatic grocery lists included.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={pantryManagementImage} 
                  alt="Pantry Management" 
                  className="w-full h-48 object-cover"
                  loading="lazy"
                  width={296}
                  height={192}
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3 text-center">Pantry Management</h3>
                  <p className="text-muted-foreground text-center leading-relaxed">
                    Keep track of your ingredients, get expiration alerts, and receive recipe suggestions based on what you have.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={recipeManagementImage} 
                  alt="Recipe Management" 
                  className="w-full h-48 object-cover"
                  loading="lazy"
                  width={296}
                  height={192}
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3 text-center">Recipe Management</h3>
                  <p className="text-muted-foreground text-center leading-relaxed">
                    Create, edit, and organize your recipes. Import from URLs or build from scratch with our intuitive editor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Features */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-foreground mb-2">Plus many more features</h3>
            <p className="text-lg text-muted-foreground">Everything you need for a complete cooking experience</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Link className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">URL Import</h4>
              <p className="text-sm text-muted-foreground">Import recipes from any cooking website</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Smart Grocery Lists</h4>
              <p className="text-sm text-muted-foreground">Pantry-aware shopping lists that know what you have</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Store className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Retailer Integration</h4>
              <p className="text-sm text-muted-foreground">Direct links to buy ingredients from your favorite stores</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wifi className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Offline Access</h4>
              <p className="text-sm text-muted-foreground">Access your recipes even when you're offline</p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-card/40 backdrop-blur-sm rounded-3xl p-12 border">
          <h2 className="text-4xl font-semibold text-foreground mb-4">Ready to transform your kitchen?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of home chefs who are already cooking smarter with SAGE. 
            Start your culinary journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-12 py-4 text-lg"
            >
              Start Free Trial
            </Button>
            <Button 
              size="lg"
              className="w-full flex items-center justify-center gap-2 px-12 py-4 bg-primary/10 text-primary rounded-2xl hover:bg-primary/20 transition-all duration-200 hover:scale-[1.02] font-medium transform text-lg"
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;